import React, { useState, useEffect } from 'react';
import { User } from '../types/user';
import { authenticatedFetch } from '../utils/api';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { AlertCircle, Bell, Calendar, Check, Clock, Globe, Mail, Phone, User as UserIcon, Sparkles } from 'lucide-react';
import { TimezoneSelect } from './ui/timezone-select';
import { fetchIntakeProfile, IntakeProfile } from '../api/intake';

interface ProfileStats {
  totalAppointments: number;
  upcomingAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
}

const fetchProfileStats = async (): Promise<ProfileStats> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/user/stats`);

  if (!response.ok) {
    throw new Error('Failed to fetch profile stats');
  }

  return response.json();
};

interface ProfilePageProps {
  user: User;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ user }) => {
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState(user.timezone || 'UTC');
  const [isSaving, setIsSaving] = useState(false);
  const [intakeProfile, setIntakeProfile] = useState<IntakeProfile | null>(null);
  const isProfileIncomplete = !user.firstName || !user.firstName.trim();
  const [profileForm, setProfileForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    timezone: user.timezone || 'Africa/Abidjan',
    phoneNumber: '',
    countryCode: '+225',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  interface NotifPrefs {
    email: { sessionReminder: boolean };
    reminderTiming: string[];
  }
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>({
    email: { sessionReminder: true },
    reminderTiming: ['24h', '1h'],
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSuccess, setNotifSuccess] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchProfileStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile stats');
      } finally {
        setIsLoading(false);
      }
    };

    const loadIntake = async () => {
      try {
        const profile = await fetchIntakeProfile();
        if (profile.status === 'completed') {
          setIntakeProfile(profile);
        }
      } catch {
        // No intake profile — that's fine
      }
    };

    const loadNotifications = async () => {
      try {
        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/user/notifications`);
        if (response.ok) {
          const data = await response.json();
          setNotifPrefs(data);
        }
      } catch {
        // Keep defaults
      }
    };

    loadStats();
    loadIntake();
    loadNotifications();
  }, []);

  const handleSaveTimezone = async () => {
    try {
      setIsSaving(true);
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        method: 'POST',
        body: JSON.stringify({
          timezone: selectedTimezone
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.reload();
      } else {
        setError('Failed to update timezone');
      }
    } catch (error) {
      console.error('Failed to update timezone', error);
      setError('Failed to update timezone');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setError(null);
    setProfileSuccess(false);
    try {
      const phoneWithCode = profileForm.phoneNumber ? `${profileForm.countryCode}${profileForm.phoneNumber}` : '';
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        body: JSON.stringify({
          email: user.email,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          timezone: profileForm.timezone,
          phoneNumber: phoneWithCode,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'An error occurred');

      localStorage.setItem('token', data.token);
      data.user.type = 'patient';
      localStorage.setItem('user', JSON.stringify(data.user));
      setProfileSuccess(true);
      // Reload to reflect updated user across the app
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setNotifSaving(true);
    setNotifSuccess(false);
    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/user/notifications`, {
        method: 'POST',
        body: JSON.stringify(notifPrefs),
      });
      if (response.ok) {
        setNotifSuccess(true);
        setTimeout(() => setNotifSuccess(false), 3000);
      } else {
        setError('Échec de la mise à jour des notifications');
      }
    } catch {
      setError('Échec de la mise à jour des notifications');
    } finally {
      setNotifSaving(false);
    }
  };

  const toggleReminderTiming = (timing: string) => {
    setNotifPrefs(prev => {
      const has = prev.reminderTiming.includes(timing);
      const updated = has
        ? prev.reminderTiming.filter(t => t !== timing)
        : [...prev.reminderTiming, timing];
      return { ...prev, reminderTiming: updated };
    });
  };

  const StatCard = ({ title, value, icon: Icon, colorClass }: { title: string; value: number; icon: any, colorClass: string }) => (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <h3 className="text-2xl font-bold">{value}</h3>
          </div>
          <div className={`p-3 rounded-full ${colorClass} bg-opacity-10`}>
            <Icon className={`h-5 w-5 ${colorClass.replace('bg-', 'text-')}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c4cc]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-[#00c4cc] to-[#008ba3] text-white pt-10 pb-20 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold">Mon Profil</h1>
          <p className="text-white/80 mt-2">Gérez vos informations personnelles et vos préférences</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 -mt-10">
        {error && (
          <Alert variant="destructive" className="mb-6 shadow-md bg-white">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Profile Info Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="shadow-lg border-none">
              <CardContent className="pt-6 text-center">
                <div className="flex flex-col items-center">
                  <Avatar className="h-24 w-24 border-4 border-white shadow-sm mb-4">
                    <AvatarImage src="" alt={user.firstName} />
                    <AvatarFallback className="bg-[#00c4cc] text-white text-2xl">
                      {getInitials(user.firstName, user.lastName || '')}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-xl font-bold text-gray-900">{user.firstName} {user.lastName}</h2>
                  <p className="text-sm text-gray-500 mb-4">{user.email}</p>

                  <div className="w-full pt-4 border-t border-gray-100 flex flex-col gap-2">
                    <p className="text-xs text-gray-500 text-center">
                      Besoin de supprimer votre compte ?
                    </p>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="w-full justify-center"
                    >
                      <a href="mailto:team@yapadra.health">
                        <Mail className="h-4 w-4 mr-2 shrink-0" />
                        Nous contacter
                      </a>
                    </Button>
                    <p className="text-xs text-gray-400 text-center break-all">
                      team@yapadra.health
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Intake Profile Card */}
            {intakeProfile?.aiProfile && (
              <Card className="shadow-sm border-[#00c4cc]/20" data-testid="intake-profile-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#00c4cc]" />
                    Mon Profil Yapadra
                  </CardTitle>
                  <CardDescription>Ton profil d'accueil personnalisé</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Stressor themes */}
                  <div className="flex flex-wrap gap-1.5">
                    {intakeProfile.aiProfile.stressorThemes.slice(0, 3).map((theme, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-cyan-50 text-[#00c4cc] rounded-full text-xs font-medium"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-[#00c4cc] text-[#00c4cc] hover:bg-[#00c4cc]/10"
                    onClick={() => { window.location.href = '/my-profile'; }}
                  >
                    Voir mon profil complet
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Details & Stats Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StatCard
                  title="Rendez-vous à venir"
                  value={stats.upcomingAppointments}
                  icon={Calendar}
                  colorClass="bg-blue-500"
                />
                <StatCard
                  title="Consultations terminées"
                  value={stats.completedAppointments}
                  icon={Clock}
                  colorClass="bg-green-500"
                />
              </div>
            )}

            {/* Complete profile banner */}
            {isProfileIncomplete && (
              <Card className="shadow-sm border-2 border-[#00c4cc]/30 bg-cyan-50/30">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00c4cc] text-white flex items-center justify-center flex-shrink-0">
                      <UserIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Complète ton profil</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Renseigne ton nom pour pouvoir réserver des séances et personnaliser ton expérience.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* General Info Form */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Informations Personnelles</CardTitle>
                <CardDescription>
                  {isProfileIncomplete
                    ? 'Complète tes informations pour commencer.'
                    : 'Mettez à jour vos coordonnées et préférences.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isProfileIncomplete ? (
                  /* Editable fields when profile is incomplete */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Prénom *</label>
                        <Input
                          type="text"
                          placeholder="Ton prénom"
                          value={profileForm.firstName}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Nom *</label>
                        <Input
                          type="text"
                          placeholder="Ton nom de famille"
                          value={profileForm.lastName}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp *
                      </label>
                      <div className="flex gap-2">
                        <select
                          className="h-10 rounded-md border border-input bg-background px-2 text-sm w-24"
                          value={profileForm.countryCode}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, countryCode: e.target.value }))}
                        >
                          <option value="+225">🇨🇮 +225</option>
                          <option value="+33">🇫🇷 +33</option>
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+44">🇬🇧 +44</option>
                          <option value="+32">🇧🇪 +32</option>
                          <option value="+41">🇨🇭 +41</option>
                          <option value="+221">🇸🇳 +221</option>
                          <option value="+223">🇲🇱 +223</option>
                          <option value="+226">🇧🇫 +226</option>
                          <option value="+228">🇹🇬 +228</option>
                          <option value="+229">🇧🇯 +229</option>
                          <option value="+237">🇨🇲 +237</option>
                        </select>
                        <Input
                          type="tel"
                          placeholder="XX XX XX XX XX"
                          value={profileForm.phoneNumber}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phoneNumber: e.target.value.replace(/[^0-9]/g, '') }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Fuseau horaire</label>
                      <TimezoneSelect
                        value={profileForm.timezone}
                        onValueChange={(value) => setProfileForm(prev => ({ ...prev, timezone: value }))}
                      />
                    </div>

                    {profileSuccess && (
                      <p className="text-green-600 text-sm text-center">Profil enregistré !</p>
                    )}

                    <Button
                      onClick={handleSaveProfile}
                      className="w-full text-white"
                      style={{ backgroundColor: '#00c4cc' }}
                      disabled={profileSaving || !profileForm.firstName.trim() || !profileForm.lastName.trim() || profileForm.phoneNumber.length < 8}
                    >
                      {profileSaving ? 'Enregistrement...' : 'Enregistrer mon profil'}
                    </Button>
                  </div>
                ) : (
                  /* Read-only fields when profile is complete */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Prénom</label>
                      <div className="flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {user.firstName}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Nom</label>
                      <div className="flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {user.lastName}
                      </div>
                    </div>
                  </div>
                )}

                {!isProfileIncomplete && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email</label>
                      <div className="flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                    </div>

                    {user.phoneNumber && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Téléphone</label>
                        <div className="flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-gray-500 bg-gray-50">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {user.phoneNumber}
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <label className="text-sm font-medium text-gray-700 block mb-2">Fuseau horaire</label>
                      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                        <div className="relative flex-1 w-full">
                          <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 z-10" />
                          <div className="[&>button]:pl-9">
                            <TimezoneSelect
                              value={selectedTimezone}
                              onValueChange={setSelectedTimezone}
                              className="w-full"
                            />
                          </div>
                        </div>
                        {selectedTimezone !== (user.timezone || 'UTC') && (
                          <Button
                            onClick={handleSaveTimezone}
                            disabled={isSaving}
                            className="w-full sm:w-auto min-w-[120px]"
                          >
                            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Les notifications de rendez-vous seront envoyées selon ce fuseau horaire.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Notifications Card */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-[#00c4cc]" />
                  Notifications
                </CardTitle>
                <CardDescription>Configurez vos rappels de séance par email.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Email reminder toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Rappels de séance par email</p>
                    <p className="text-xs text-gray-500 mt-0.5">Recevez un email avant chaque séance</p>
                  </div>
                  <button
                    onClick={() => setNotifPrefs(prev => ({
                      ...prev,
                      email: { ...prev.email, sessionReminder: !prev.email.sessionReminder }
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      notifPrefs.email.sessionReminder ? 'bg-[#00c4cc]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        notifPrefs.email.sessionReminder ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Reminder timing */}
                {notifPrefs.email.sessionReminder && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Quand souhaitez-vous être rappelé ?</p>
                    <div className="flex flex-wrap gap-2">
                      {([
                        { key: '48h',   label: '2 jours avant' },
                        { key: '24h',   label: '24h avant' },
                        { key: '2h',    label: '2h avant' },
                        { key: '1h',    label: '1h avant' },
                        { key: '30min', label: '30 min avant' },
                      ] as const).map(({ key, label }) => {
                        const active = notifPrefs.reminderTiming.includes(key);
                        return (
                          <button
                            key={key}
                            onClick={() => toggleReminderTiming(key)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                              active
                                ? 'bg-[#00c4cc] text-white border-[#00c4cc] shadow-sm'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-[#00c4cc] hover:text-[#00c4cc]'
                            }`}
                          >
                            {active && <Check size={13} strokeWidth={3} />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400">Sélectionnez un ou plusieurs créneaux.</p>
                  </div>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={handleSaveNotifications}
                    disabled={notifSaving}
                    className="text-white"
                    style={{ backgroundColor: '#00c4cc' }}
                  >
                    {notifSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                  {notifSuccess && (
                    <span className="text-sm text-green-600">Préférences enregistrées !</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};